/**
 * useKeyboardNav — keyboard alternative for tile moves in edit mode.
 *
 * Glance-spec keyboard a11y: select a tile, arrow keys move by one cell,
 * Enter commits, Escape cancels. Visible focus state is provided via CSS.
 *
 * Usage:
 *   const kbNav = useKeyboardNav(board, commitMove);
 *   // attach kbNav.onKeyDown to the grid element
 *   // kbNav.selectedId is the currently focused tile
 *
 * Immutability: state is replaced wholesale, never patched.
 */
import { useState, useCallback } from 'react';
import type { Board, TileSize } from 'briefpane/schema';
import { MAX_GRID_ROWS, sizeToWH } from 'briefpane/schema';

export interface KeyboardNavState {
  /** The tileId currently selected by keyboard, or null. */
  readonly selectedId: string | null;
  /** Ghost position while moving (before commit). */
  readonly pendingPos: { x: number; y: number } | null;
  /** Is the user actively moving the selected tile (arrow-key mode)? */
  readonly isMoving: boolean;
}

export interface KeyboardNavHandlers {
  readonly state: KeyboardNavState;
  /** Select a tile by id (focus it for keyboard control). */
  readonly select: (tileId: string | null) => void;
  /** KeyDown handler to attach to the grid container (or each tile). */
  readonly onKeyDown: (e: React.KeyboardEvent, tileId?: string) => void;
}

/**
 * useKeyboardNav — keyboard tile selection + arrow-key move + enter/escape.
 *
 * @param board - current board (read-only, used to look up positions)
 * @param commitMove - called with (tileId, x, y) when Enter confirms
 * @param editMode - only active in edit mode
 * @param commitResize - optional: called with (tileId, size) on Shift+Arrow resize
 * @param getTileAllowedSizes - optional: returns allowed sizes for a tile id
 */
export function useKeyboardNav(
  board: Board,
  commitMove: (tileId: string, x: number, y: number) => void,
  editMode: boolean,
  commitResize?: (tileId: string, size: TileSize) => void,
  getTileAllowedSizes?: (tileId: string) => readonly TileSize[],
): KeyboardNavHandlers {
  const [state, setState] = useState<KeyboardNavState>({
    selectedId: null,
    pendingPos: null,
    isMoving: false,
  });

  const select = useCallback((tileId: string | null) => {
    setState({ selectedId: tileId, pendingPos: null, isMoving: false });
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent, tileId?: string) => {
      if (!editMode) return;

      // If a tileId is provided and no tile is selected, select this one.
      const activeId = state.selectedId ?? tileId ?? null;
      if (!activeId) return;

      // Enter: start moving, or commit pending move
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!state.isMoving) {
          // Begin move mode: load current position as pending
          const entry = board.layout.find((l) => l.tileId === activeId);
          if (entry) {
            setState((prev) => ({
              ...prev,
              selectedId: activeId,
              pendingPos: { x: entry.x, y: entry.y },
              isMoving: true,
            }));
          }
        } else if (state.pendingPos) {
          // Commit the move
          commitMove(activeId, state.pendingPos.x, state.pendingPos.y);
          setState({ selectedId: activeId, pendingPos: null, isMoving: false });
        }
        return;
      }

      // Escape: cancel pending move
      if (e.key === 'Escape') {
        e.preventDefault();
        setState({ selectedId: null, pendingPos: null, isMoving: false });
        return;
      }

      // Shift+Arrow: keyboard resize parity (a11y)
      if (e.shiftKey && commitResize && getTileAllowedSizes && activeId) {
        const arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (arrowKeys.includes(e.key)) {
          e.preventDefault();
          const tile = board.tiles.find((t) => t.id === activeId);
          if (!tile) return;
          const { w: currentW, h: currentH } = sizeToWH(tile.display.size);
          const allowed = getTileAllowedSizes(activeId);
          let targetSize: TileSize | null = null;
          if (e.key === 'ArrowRight') {
            // Find next wider size (minimum w that is > currentW)
            const wider = allowed.filter((s) => { const { w } = sizeToWH(s); return w > currentW; });
            targetSize = wider.length > 0 ? wider.reduce((a, b) => sizeToWH(a).w <= sizeToWH(b).w ? a : b) : null;
          } else if (e.key === 'ArrowLeft') {
            // Find next narrower size (maximum w that is < currentW)
            const narrower = allowed.filter((s) => { const { w } = sizeToWH(s); return w < currentW; });
            targetSize = narrower.length > 0 ? narrower.reduce((a, b) => sizeToWH(a).w >= sizeToWH(b).w ? a : b) : null;
          } else if (e.key === 'ArrowDown') {
            // Find next taller size (minimum h that is > currentH)
            const taller = allowed.filter((s) => { const { h } = sizeToWH(s); return h > currentH; });
            targetSize = taller.length > 0 ? taller.reduce((a, b) => sizeToWH(a).h <= sizeToWH(b).h ? a : b) : null;
          } else if (e.key === 'ArrowUp') {
            // Find next shorter size (maximum h that is < currentH)
            const shorter = allowed.filter((s) => { const { h } = sizeToWH(s); return h < currentH; });
            targetSize = shorter.length > 0 ? shorter.reduce((a, b) => sizeToWH(a).h >= sizeToWH(b).h ? a : b) : null;
          }
          if (targetSize !== null) {
            commitResize(activeId, targetSize);
          }
          return;
        }
      }

      // Arrow keys: move the pending position by one cell (only while isMoving)
      if (!state.isMoving || !state.pendingPos) return;

      const { x, y } = state.pendingPos;
      let newX = x;
      let newY = y;

      // Look up the moving tile's footprint so we clamp at the right edge.
      const entry = board.layout.find((l) => l.tileId === activeId);
      const w = entry?.w ?? 1;
      const h = entry?.h ?? 1;

      if (e.key === 'ArrowLeft') { e.preventDefault(); newX = Math.max(0, x - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); newX = Math.min(board.grid.cols - w, x + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); newY = Math.max(0, y - 1); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); newY = Math.min(MAX_GRID_ROWS - h, y + 1); }
      else return;

      setState((prev) => ({
        ...prev,
        pendingPos: { x: newX, y: newY },
      }));
    },
    [board.layout, board.tiles, commitMove, editMode, state, commitResize, getTileAllowedSizes],
  );

  return { state, select, onKeyDown };
}
